import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from './axiosConfig';
import FieldInput from './CustomFieldInput';

// 🎯 نقطة إدخال حقول كستم جوه شاشة موجودة فعلاً - بتتحط جوه section حقيقي
// (أو نص التاب لو section=null) بدل ما تتحط في تاب منفصل مبتور عن باقي
// الحقول. لو مفيش حقول متعرّفة على الـ anchor ده أصلاً، الكومبوننت مايرندرش
// أي حاجة - الشاشات اللي لسه مالهاش حقول كستم تفضل زي ما هي بالظبط.
const CustomFieldSlot = ({ coreModelName, tab, section, recordId, disabled = false }) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  // 🎯 الحفظ عن طريق onBlur بس كان بيفشل بصمت لأي حقل اختياره بيقفل عنصر
  // كان عليه الفوكس في نفس اللحظة (Link/Table/Image - أي حقل بيفتح مودال
  // ويقفله فور الاختيار): إقفال المودال بيشيل الـ input اللي كان عليه
  // الفوكس من الـ DOM قبل ما event الـ blur يعدّي فعليًا خلال نظام الأحداث
  // بتاع React، فـ onBlur بتاع الـ div مابيتنادش خالص - القيمة تفضل في الـ
  // state المحلي بس ومتترجعش تختفي إلا بعد ريفريش. الحل: مش نعتمد على
  // blur لوحده - كل تغيير قيمة بيحفظ بعد debounce قصير كمان، وblur بيفضل
  // موجود عشان يحفظ فورًا للحقول النصية العادية (بيتاخد الأولوية لو حصل)
  const saveTimerRef = useRef(null);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => {
    if (!recordId) { setLoading(false); return; }
    let alive = true;
    api.get('/custom-field-values', {
      params: { core_model_name: coreModelName, core_record_id: recordId, tab, section: section || '__end_of_tab__' },
    })
      .then((res) => {
        if (!alive) return;
        const rows = res.data || [];
        setFields(rows);
        setValues(Object.fromEntries(rows.map((f) => [f.field_id, f.value])));
      })
      .catch(() => { if (alive) setFields([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [coreModelName, tab, section, recordId]);

  const doSave = useCallback(async (vals) => {
    if (disabledRef.current) return;
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    try {
      await api.put('/custom-field-values', {
        core_model_name: coreModelName, core_record_id: recordId, values: vals,
      });
      setDirty(false);
    } catch (err) { /* silent - field stays editable, retried on next change/blur */ }
  }, [coreModelName, recordId]);

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

  const setValue = (fieldId, value) => {
    setValues((prev) => {
      const next = { ...prev, [fieldId]: value };
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => doSave(next), 500);
      return next;
    });
    setDirty(true);
  };

  const handleBlurSave = () => {
    if (!dirty || disabled) return;
    doSave(values);
  };

  if (loading || disabled || fields.length === 0) return null;

  return (
    <>
      {fields.map((field) => {
        const isBoxType = field.field_type === 'table' || field.field_type === 'image';
        return (
          <div
            key={field.field_id}
            onBlur={handleBlurSave}
            className={`flex flex-col gap-1 px-1 py-1.5 rounded-sm transition-all ${isBoxType ? 'md:col-span-full' : 'border-b border-line-subtle group hover:bg-primary-soft focus-within:border-primary'}`}
          >
            <label className={`${field.is_required ? 'text-status-danger' : 'text-ink-secondary'} font-bold text-[0.8] uppercase tracking-tighter`}>
              {t(`custom.field.${field.field_name}`, field.label)}
            </label>
            <FieldInput
              field={field}
              value={values[field.field_id]}
              onChange={(v) => setValue(field.field_id, v)}
            />
          </div>
        );
      })}
    </>
  );
};

export default CustomFieldSlot;
